/* eslint-disable max-len */
import { useContext, useEffect } from 'react';
import proptypes from 'prop-types';
import {
  extend,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useStoreId, ExperienceContext } from '@thd-nucleus/experience-context';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { copy } from '@thd-olt-functional/utils';
import {
  formatProductLabel, generateProductDescription, generateProductTitle,
  getBrandName,
  getBundleVideo,
  getImagePaths,
  getProductType
} from '../../helpers/product-helpers';
import {
  getVideoStructuredData,
  getBreadcrumbStructuredData,
  getProductStructuredData
} from '../../structured-data';
import {
  HD_URL,
  OPEN_GRAPH_FACEBOOK_ADMIN,
  ANDROID_HREF
} from '../../constants';
import {
  generateTags,
  isTitleValid,
  isCanonicalUrlValid
} from '../../utils';
import {
  productBundleModel,
  productsModel,
  reviewsModel
} from '../../models';

const { bool, string, shape, arrayOf } = proptypes;

const bundleInformationMetaDataHandler = ({ data, props = {} }) => {
  if (!data?.product) {
    return [];
  }

  const { product = {}, products, reviews: reviewsData } = data;
  const {
    itemId,
    details,
    identifiers = {},
    availabilityType,
    media,
    taxonomy,
    pricing,
    specificationGroup,
    info,
    bundleSpecificationDetails,
    seo
  } = product || {};

  // ---- Flags & Labels ----
  const isKitchenBundle = bundleSpecificationDetails?.experienceType?.toLowerCase() === 'kitchen';
  const isPPQ = props?.ppq;
  const brandName = getBrandName(product, products);
  const productLabel = formatProductLabel(brandName, identifiers?.productLabel, identifiers?.modelNumber);
  const returnable = info?.returnable;

  // ---- Title & Description Resolution ----

  const { pageNumber, pageTitle, pageDescription } = props;
  const title = generateProductTitle({
    productLabel,
    pageNumber,
    pageTitle,
    isPPQ
  });

  const isTitlePageValid = isTitleValid(title);

  const productDescription = details?.description;
  const seoDescription = seo?.seoDescription;
  const description = generateProductDescription({
    pageDescription,
    seoDescription,
    productDescription,
    isPPQ
  });

  // ---- Canonical ----
  const { canonicalUrl = '' } = identifiers;
  const canonical = `${HD_URL}${canonicalUrl}`;

  const isCanonicalValid = isCanonicalUrlValid(canonicalUrl);

  // ---- Schema Structured Data ----
  const productStructure = getProductStructuredData({
    reviewsData,
    identifiers,
    media,
    productLabel,
    productDescription,
    pricing,
    specifications: specificationGroup,
    availabilityType,
    schemaExclude: props.schema,
    ppq: props.ppq,
    returnable,
    excludeReviews: isKitchenBundle
  });

  const breadcrumbStructuredData = getBreadcrumbStructuredData({
    canonical,
    breadcrumbs: copy(taxonomy?.breadCrumbs || []).data,
    title,
    brandName,
    brandLinkUrl: taxonomy?.brandLinkUrl
  });

  const video = getBundleVideo(media, bundleSpecificationDetails, products);
  const videoStructuredData = video?.length
    ? getVideoStructuredData({
      videos: video,
      productLabel
    })
    : undefined;

  const schemas = [
    {
      content: productStructure,
      id: 'productStructureData'
    },
    {
      content: breadcrumbStructuredData,
      id: 'breadcrumbStructureData'
    },
    ...(videoStructuredData
      ? [{
        content: videoStructuredData,
        id: 'videoStructuredData'
      }]
      : [])
  ];

  // ---- Other Meta Tags ----
  const isBundle = canonicalUrl?.startsWith('/p/sets/');

  const isNoFollow = availabilityType?.discontinued;
  const type = getProductType(isPPQ, isBundle);

  const alternate = `${ANDROID_HREF}product/${itemId}`;
  const primaryImage = media?.image?.url;
  const firstProductInBundle = products?.[0]?.media?.images?.[0]?.url; // LCP image url for kitchen bundles comes from first product in bundle

  const preloadImages = getImagePaths({
    primaryImage: isKitchenBundle && firstProductInBundle ? firstProductInBundle : primaryImage,
    size: isKitchenBundle ? 1000 : 600,
    imgID: 'bundleLCPImage'
  });

  // ---- Generate Meta Tags ----
  return generateTags({
    ...(isTitlePageValid && {
      title,
      ogTitle: title
    }),
    ...(isCanonicalValid && {
      canonical,
      ogUrl: canonical
    }),
    description,
    ogDescription: description,
    ogType: type,
    ogImage: primaryImage,
    ogSiteName: 'The Home Depot',
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    preloadImages,
    alternate,
    robots: isNoFollow,
    schemas
  });
};

const BundleInformationMetadata = ({
  itemId = '',
  pageDescription = '',
  pageNumber,
  pageTitle = '',
  bundleProduct = {},
  bundleProducts = [],
  requestVariables = {},
  skipProductsRequest = false,
  ppq = false,
  questionsCanonical = false,
  reviewsCanonical = false,
  hideIfHDPPSku = false,
}) => {
  const pageSize = 30;
  const storeId = useStoreId();
  const { deliveryZip } = useContext(ExperienceContext);
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bundle-metadata.ready');
  }, []);

  const productOptions = {
    variables: {
      itemId,
      storeId,
      zipCode: deliveryZip
    },
    skip: skipProductsRequest
  };

  const startIndex = (pageNumber - 1) * pageSize + (pageNumber > 1 ? 1 : 0);
  const reviewsOptions = {
    variables: {
      itemId,
      ...(pageNumber && {
        startIndex,
        pagesize: pageSize,
        recfirstpage: pageSize
      })
    }
  };

  const { data, variables } = useDataModel('product', productOptions);

  const bundleProductIds = data?.product?.bundleSpecificationDetails?.components;
  const { data: productsData } = useDataModel('products', {
    variables: {
      itemIds: bundleProductIds?.map(({ defaultProductId }) => defaultProductId)
    },
    skip: skipProductsRequest || !bundleProductIds?.map(({ defaultProductId }) => defaultProductId)
  });

  const { data: reviewData } = useDataModel('reviews', reviewsOptions);
  const props = {
    reviewsCanonical,
    pageDescription,
    pageNumber,
    pageTitle,
    questionsCanonical,
    ppq,
    hideIfHDPPSku
  };

  const bundleProductData = skipProductsRequest ? bundleProduct : data?.product;
  const bundleProductsData = skipProductsRequest ? bundleProducts : productsData?.products;
  const bundleProductRequestVariables = skipProductsRequest ? requestVariables : variables;

  const helmetData = {
    product: bundleProductData,
    products: bundleProductsData,
    reviews: !(props?.hideIfHDPPSku && bundleProductData?.identifiers?.skuClassification === 'hdpp') && reviewData?.reviews
  };
  const deps = bundleProductData && bundleProductsData && reviewData?.reviews && bundleProductData?.itemId;
  useHelmet('bundleInformationMetadata',
    { data: helmetData,
      props,
      bundleProductRequestVariables,
      skipCleanup: true }, bundleInformationMetaDataHandler, [deps]);
  return null;
};

BundleInformationMetadata.displayName = 'BundleInformationMetadata';

BundleInformationMetadata.dataModel = extend(
  productBundleModel,
  reviewsModel,
  productsModel
);

BundleInformationMetadata.propTypes = {
  itemId: string.isRequired,
  reviewsCanonical: bool,
  pageNumber: string,
  pageTitle: string,
  pageDescription: string,
  questionsCanonical: bool,
  ppq: bool,
  hideIfHDPPSku: bool,
  bundleProduct: shape({}),
  bundleProducts: arrayOf(shape()),
  requestVariables: shape({}),
  skipProductsRequest: bool
};

export { BundleInformationMetadata };
